// Analytics utility for tracking user events
// Consent-gated: services are only initialized after cookie consent

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
    fbq: (...args: any[]) => void;
    hj: (...args: any[]) => void;
  }
}

/**
 * Da dove nasce l'intento di acquisto. Tipizzato e non stringa libera: questi
 * valori finiscono nei report, e un refuso crea una riga separata che nessuno
 * nota (es. "chat_popup" vs "chat-popup" contati come due sorgenti diverse).
 *
 * - `credits_page`      listino completo in /premium-status: scelta deliberata
 * - `out_of_credits`    popup a crediti esauriti durante la chat
 * - `insufficient_for_action` popup "ti mancano N crediti" su un'azione precisa
 *   (foto): l'intento piu' caldo, l'utente stava gia' facendo qualcosa
 * - `subscription_page` abbonamento premium
 */
export type CheckoutSource =
  | "credits_page"
  | "out_of_credits"
  | "insufficient_for_action"
  | "subscription_page";

class AnalyticsManager {
  private gaInitialized = false;
  private fbInitialized = false;
  private hotjarInitialized = false;
  private settings: { [key: string]: string } = {};
  private settingsLoaded = false;

  getGaMeasurementId(): string | null {
    return this.settings.GOOGLE_ANALYTICS_ID || null;
  }
  // Id dell'utente loggato, per GA4 User-ID. Tenuto qui perche' consenso e
  // sessione arrivano in ordine non garantito: se l'utente e' noto prima del
  // consenso, initializeGA() lo legge; se il consenso arriva prima, setUserId()
  // lo applica a GA gia' attivo. Senza questo GA conta browser, non persone:
  // stesso utente da telefono e desktop = due "nuovi utenti", e la metrica
  // "returning users" risulta una sottostima di entita' ignota.
  private userId: string | null = null;

  // Fonte d'iscrizione dell'utente loggato (users.signup_source), mandata a
  // GA4 come proprieta' utente `signup_source`. Il cookie GA cambia con il
  // browser (chi si iscrive dal browser interno di Instagram e poi compra da
  // Safari e' per GA un altro utente, con fonte "direct"); questa viene
  // dall'account, quindi tentativi e acquisti restano sulla fonte vera.
  private signupSource: string | null = null;

  // Fetch analytics settings (called once).
  //
  // Reads the public endpoint, which exposes only the three measurement IDs
  // from the same analytics_settings table the server-side Measurement
  // Protocol reads (ga-measurement.ts) — one source of truth for the ID.
  //
  // Previously this hit /api/admin/analytics-settings, which is behind
  // adminAuth: every non-admin visitor got a 401 and returned silently, so
  // GA/Meta/Hotjar only ever initialized while an admin was browsing and no
  // real user traffic was tracked.
  async loadSettings() {
    if (this.settingsLoaded) return;

    try {
      const response = await fetch("/api/analytics/public");
      if (!response.ok) return;

      const settings = await response.json();
      if (!Array.isArray(settings)) return;

      settings.forEach((setting: any) => {
        if (setting?.key) this.settings[setting.key] = setting.value;
      });
      this.settingsLoaded = true;
    } catch (error) {
      console.error("Failed to load analytics settings:", error);
    }
  }

  // --- Consent-gated activation ---

  async enableAnalytics() {
    await this.loadSettings();
    if (this.settings.GOOGLE_ANALYTICS_ID) {
      this.initializeGA(this.settings.GOOGLE_ANALYTICS_ID);
    }
  }

  disableAnalytics() {
    if (this.gaInitialized && this.settings.GOOGLE_ANALYTICS_ID) {
      // Disable GA by setting the opt-out window property
      (window as any)[`ga-disable-${this.settings.GOOGLE_ANALYTICS_ID}`] = true;
    }
  }

  async enableMarketing() {
    await this.loadSettings();
    if (this.settings.FACEBOOK_PIXEL_ID) {
      this.initializeFacebookPixel(this.settings.FACEBOOK_PIXEL_ID);
    }
    if (this.settings.HOTJAR_ID) {
      this.initializeHotjar(this.settings.HOTJAR_ID);
    }
  }

  disableMarketing() {
    // FB Pixel e Hotjar non hanno un meccanismo di disable runtime nativo.
    // La revoca effettiva avviene al prossimo page load (gli script non vengono caricati).
    this.fbInitialized = false;
    this.hotjarInitialized = false;
  }

  // --- Initialize individual services ---

  private initializeGA(measurementId: string) {
    if (this.gaInitialized) return;

    // Remove any previous opt-out flag
    (window as any)[`ga-disable-${measurementId}`] = false;

    const script1 = document.createElement("script");
    script1.async = true;
    script1.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    document.head.appendChild(script1);

    // L'user_id va passato nella config iniziale quando gia' noto, cosi' il
    // primo page_view della sessione e' gia' attribuito alla persona.
    const userIdConfig = this.userId
      ? `, { user_id: ${JSON.stringify(this.userId)} }`
      : "";
    // Stesso discorso per la fonte d'iscrizione (vedi signupSource).
    const userPropertiesSet = this.signupSource
      ? `gtag('set', 'user_properties', { signup_source: ${JSON.stringify(this.signupSource)} });`
      : "";

    const script2 = document.createElement("script");
    script2.textContent = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      ${userPropertiesSet}
      gtag('config', '${measurementId}'${userIdConfig});
    `;
    document.head.appendChild(script2);

    this.gaInitialized = true;
  }

  // --- GA4 User-ID ---

  // Associa gli eventi successivi all'utente loggato. Chiamata dall'hook di
  // sessione (useAuth) a ogni caricamento in cui l'utente risulta autenticato,
  // non solo al login esplicito: chi torna con la sessione gia' attiva — cioe'
  // proprio l'utente di ritorno che vogliamo misurare — non passa dal form di
  // login e altrimenti resterebbe anonimo.
  setUserId(userId: string, signupSource?: string | null) {
    if (!userId) return;
    // GA4 tronca le proprieta' utente a 36 caratteri: meglio farlo qui, in
    // modo prevedibile.
    const source = signupSource ? signupSource.slice(0, 36) : null;
    if (this.userId === userId && this.signupSource === source) return;
    this.userId = userId;
    this.signupSource = source;

    // Se GA non e' ancora partito (consenso non dato, o non ancora concesso),
    // id e fonte restano in attesa: li applichera' initializeGA().
    if (this.gaInitialized && window.gtag) {
      const measurementId = this.settings.GOOGLE_ANALYTICS_ID;
      if (measurementId) {
        // Prima la proprieta', poi la config: il page_view che la config
        // puo' generare la porta gia' con se'.
        window.gtag("set", "user_properties", { signup_source: source });
        window.gtag("config", measurementId, { user_id: userId });
      }
    }
  }

  // Logout: sgancia gli eventi successivi dall'utente, altrimenti restano
  // attribuiti a lui anche se al browser si siede un'altra persona.
  clearUserId() {
    this.userId = null;
    this.signupSource = null;
    if (this.gaInitialized && window.gtag) {
      const measurementId = this.settings.GOOGLE_ANALYTICS_ID;
      if (measurementId) {
        window.gtag("set", "user_properties", { signup_source: null });
        window.gtag("config", measurementId, { user_id: null });
      }
    }
  }

  private initializeFacebookPixel(pixelId: string) {
    if (this.fbInitialized) return;

    const script = document.createElement("script");
    script.textContent = `
      !function(f,b,e,v,n,t,s)
      {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};
      if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
      n.queue=[];t=b.createElement(e);t.async=!0;
      t.src=v;s=b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t,s)}(window, document,'script',
      'https://connect.facebook.net/en_US/fbevents.js');
      fbq('init', '${pixelId}');
      fbq('track', 'PageView');
    `;
    document.head.appendChild(script);

    this.fbInitialized = true;
  }

  private initializeHotjar(hotjarId: string) {
    if (this.hotjarInitialized) return;

    const script = document.createElement("script");
    script.textContent = `
      (function(h,o,t,j,a,r){
        h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
        h._hjSettings={hjid:${hotjarId},hjsv:6};
        a=o.getElementsByTagName('head')[0];
        r=o.createElement('script');r.async=1;
        r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
        a.appendChild(r);
      })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');
    `;
    document.head.appendChild(script);

    this.hotjarInitialized = true;
  }

  // --- Tracking methods (only fire if service is initialized) ---

  trackPageView(url: string) {
    if (this.gaInitialized && window.gtag) {
      const measurementId = this.settings.GOOGLE_ANALYTICS_ID;
      if (measurementId) {
        // user_id va ripetuto: ogni gtag('config') sostituisce i parametri
        // precedenti, quindi ometterlo qui scollegherebbe dall'utente tutti
        // gli eventi dopo il primo cambio pagina.
        window.gtag("config", measurementId, {
          page_path: url,
          ...(this.userId ? { user_id: this.userId } : {}),
        });
      }
    }

    if (this.fbInitialized && window.fbq) {
      window.fbq("track", "PageView");
    }
  }

  trackEvent(
    action: string,
    category?: string,
    label?: string,
    value?: number
  ) {
    if (this.gaInitialized && window.gtag) {
      window.gtag("event", action, {
        event_category: category,
        event_label: label,
        value: value,
      });
    }

    if (this.fbInitialized && window.fbq) {
      window.fbq("trackCustom", action, { category, label, value });
    }
  }

  trackRegistration(userId: string, method: string = "email") {
    if (userId) this.setUserId(userId);
    this.trackEvent("sign_up", "user", method);

    if (this.fbInitialized && window.fbq) {
      window.fbq("track", "CompleteRegistration", {
        content_name: "User Registration",
        method: method,
      });
    }
  }

  trackLogin(userId: string, method: string = "email") {
    if (userId) this.setUserId(userId);
    this.trackEvent("login", "user", method);
  }

  trackProfileView(profileId: string) {
    this.trackEvent("view_profile", "engagement", profileId);

    if (this.fbInitialized && window.fbq) {
      window.fbq("track", "ViewContent", {
        content_type: "profile",
        content_ids: [profileId],
      });
    }
  }

  trackMessage(recipientId: string) {
    this.trackEvent("send_message", "communication", recipientId);

    if (this.fbInitialized && window.fbq) {
      window.fbq("track", "Contact", { content_name: "Message Sent" });
    }
  }

  trackCreditPurchase(amount: number, cost: number) {
    this.trackEvent(
      "purchase_credits",
      "monetization",
      `${amount}_credits`,
      cost
    );

    if (this.fbInitialized && window.fbq) {
      window.fbq("track", "Purchase", {
        value: cost,
        currency: "EUR",
        content_name: `${amount} Credits`,
      });
    }
  }

  trackSubscription(planName: string, cost: number) {
    this.trackEvent("subscribe", "monetization", planName, cost);

    if (this.fbInitialized && window.fbq) {
      window.fbq("track", "Subscribe", {
        value: cost,
        currency: "EUR",
        content_name: planName,
      });
    }
  }

  trackGiftSent(giftType: string, cost: number) {
    this.trackEvent("send_gift", "engagement", giftType, cost);
  }

  // --- GA4 ecommerce events (use the standard items[] schema) ---

  // User starts a paid flow (clicks "Abbonati" / "Acquista crediti").
  //
  // `source` dice DA DOVE parte l'intento, non cosa si compra: senza, i tre
  // punti d'ingresso mandano payload identici e in GA4 sono indistinguibili.
  // Serve a capire quale contesto converte — un popup che interrompe un'azione
  // ("ti mancano 8 crediti per questa foto") e una scelta deliberata dal
  // listino sono intenti molto diversi, e vanno letti separati.
  // Su GA4 arriva come parametro dell'evento (va registrato come dimensione
  // personalizzata in Admin per comparire nei report); su Meta come content_category.
  trackBeginCheckout(item: {
    id: string;
    name: string;
    price: number;
    category?: string;
    source?: CheckoutSource;
  }) {
    if (this.gaInitialized && window.gtag) {
      window.gtag("event", "begin_checkout", {
        currency: "EUR",
        value: item.price,
        ...(item.source ? { checkout_source: item.source } : {}),
        items: [
          {
            item_id: item.id,
            item_name: item.name,
            item_category: item.category,
            price: item.price,
            quantity: 1,
          },
        ],
      });
    }

    if (this.fbInitialized && window.fbq) {
      window.fbq("track", "InitiateCheckout", {
        value: item.price,
        currency: "EUR",
        content_name: item.name,
        content_ids: [item.id],
        ...(item.source ? { content_category: item.source } : {}),
      });
    }
  }

  // Purchase confirmed (fired on the success page after returning from PSP).
  //
  // `skipGa` exists because GA4 already receives this purchase server-side from
  // the payment webhook (ga-measurement.ts), which is the source of truth for
  // revenue: firing it here too would double-count it. The success page passes
  // skipGa, so this call carries the Meta Pixel only. Meta's own server-side
  // event (meta-capi.ts) is deduped by event id instead, not suppressed — see
  // the fbq call below. Callers without a server-side GA event leave it unset.
  trackPurchase(order: {
    transactionId: string;
    name: string;
    price: number;
    category?: string;
    isSubscription?: boolean;
    skipGa?: boolean;
  }) {
    if (!order.skipGa && this.gaInitialized && window.gtag) {
      window.gtag("event", "purchase", {
        transaction_id: order.transactionId,
        currency: "EUR",
        value: order.price,
        items: [
          {
            item_id: order.name,
            item_name: order.name,
            item_category: order.category,
            price: order.price,
            quantity: 1,
          },
        ],
      });

      if (order.isSubscription) {
        window.gtag("event", "subscribe", {
          currency: "EUR",
          value: order.price,
          item_name: order.name,
        });
      }
    }

    // `eventID` is the transaction id, so Meta collapses repeats of the same
    // order into one conversion: a reload that slipped past the caller's guard,
    // or the same purchase arriving twice. It is ALSO what the server-side
    // Conversions API sends as `event_id` (meta-capi.ts) — the two must be
    // built from the same id, or one order is counted as two conversions.
    // That shared id is `credit_transactions.analytics_transaction_id`.
    if (this.fbInitialized && window.fbq) {
      window.fbq(
        "track",
        "Purchase",
        {
          value: order.price,
          currency: "EUR",
          content_name: order.name,
        },
        { eventID: `purchase_${order.transactionId}` }
      );
      if (order.isSubscription) {
        window.fbq(
          "track",
          "Subscribe",
          {
            value: order.price,
            currency: "EUR",
            content_name: order.name,
          },
          { eventID: `subscribe_${order.transactionId}` }
        );
      }
    }
  }

  trackLike(profileId: string) {
    this.trackEvent("add_to_wishlist", "engagement", profileId);

    if (this.fbInitialized && window.fbq) {
      window.fbq("track", "AddToWishlist", {
        content_type: "profile",
        content_ids: [profileId],
      });
    }
  }
}

// Read the GA4 client_id from the `_ga` cookie (format: GA1.1.XXXXXX.YYYYYY).
// Returns the "XXXXXX.YYYYYY" portion, which is what the Measurement Protocol
// expects as client_id. Null if GA hasn't set the cookie yet (no consent).
export function getGaClientId(): string | null {
  try {
    const match = document.cookie.match(/(?:^|;\s*)_ga=([^;]+)/);
    if (!match) return null;
    const parts = decodeURIComponent(match[1]).split(".");
    if (parts.length < 4) return null;
    return `${parts[2]}.${parts[3]}`;
  } catch {
    return null;
  }
}

// Read the current GA4 session_id from the `_ga_<ID>` cookie of our stream.
// It travels through CCBill as a pass-through so the server-side purchase
// (ga-measurement.ts) lands in the checkout session and keeps its traffic
// source; without it GA4 files every purchase under "(not set)".
// Two cookie formats exist: GS1.1.<session_id>.<n>... and the newer
// GS2.1.s<session_id>$o<n>$g... Null without consent or before GA loads.
export function getGaSessionId(): string | null {
  try {
    const measurementId = analytics.getGaMeasurementId();
    if (!measurementId) return null;
    const name = `_ga_${measurementId.replace(/^G-/, "")}`;
    const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]+)`));
    if (!match) return null;
    const field = decodeURIComponent(match[1]).split(".")[2];
    if (!field) return null;
    const sessionId = field.startsWith("s") ? field.slice(1).split("$")[0] : field;
    return /^\d{1,20}$/.test(sessionId) ? sessionId : null;
  } catch {
    return null;
  }
}

// Meta's browser cookies, needed by the Conversions API to match a server-side
// event to the same person the Pixel saw. Both are set by the Pixel itself, so
// both are null when marketing consent was denied (the Pixel never loads) —
// which is exactly when no server-side event should be sent either.
//
// `_fbp` identifies the browser; `_fbc` is written only when the visitor
// arrived from a Facebook ad click (fbclid in the URL), so it is routinely
// absent on organic traffic and its absence is not an error.
export function getFbTrackingIds(): { fbp: string | null; fbc: string | null } {
  try {
    const read = (name: string) => {
      const m = document.cookie.match(
        new RegExp(`(?:^|;\\s*)${name}=([^;]+)`)
      );
      return m ? decodeURIComponent(m[1]) : null;
    };
    return { fbp: read("_fbp"), fbc: read("_fbc") };
  } catch {
    return { fbp: null, fbc: null };
  }
}

// Singleton
const analytics = new AnalyticsManager();

// Consent-gated enable/disable (called by cookie-banner.tsx)
export const enableAnalytics = () => analytics.enableAnalytics();
export const disableAnalytics = () => analytics.disableAnalytics();
export const enableMarketing = () => analytics.enableMarketing();
export const disableMarketing = () => analytics.disableMarketing();

// Tracking convenience functions
export const trackPageView = (url: string) => analytics.trackPageView(url);
export const trackEvent = (
  action: string,
  category?: string,
  label?: string,
  value?: number
) => analytics.trackEvent(action, category, label, value);
export const trackRegistration = (userId: string, method?: string) =>
  analytics.trackRegistration(userId, method);
export const trackLogin = (userId: string, method?: string) =>
  analytics.trackLogin(userId, method);
// GA4 User-ID: chiamata dall'hook di sessione, non solo dai form di login.
export const setAnalyticsUserId = (userId: string, signupSource?: string | null) =>
  analytics.setUserId(userId, signupSource);
export const clearAnalyticsUserId = () => analytics.clearUserId();
export const trackProfileView = (profileId: string) =>
  analytics.trackProfileView(profileId);
export const trackMessage = (recipientId: string) =>
  analytics.trackMessage(recipientId);
export const trackCreditPurchase = (amount: number, cost: number) =>
  analytics.trackCreditPurchase(amount, cost);
export const trackSubscription = (planName: string, cost: number) =>
  analytics.trackSubscription(planName, cost);
export const trackGiftSent = (giftType: string, cost: number) =>
  analytics.trackGiftSent(giftType, cost);
export const trackBeginCheckout = (item: {
  id: string;
  name: string;
  price: number;
  category?: string;
  source?: CheckoutSource;
}) => analytics.trackBeginCheckout(item);
export const trackPurchase = (order: {
  transactionId: string;
  name: string;
  price: number;
  category?: string;
  isSubscription?: boolean;
  skipGa?: boolean;
}) => analytics.trackPurchase(order);
export const trackLike = (profileId: string) => analytics.trackLike(profileId);

// Legacy export — no longer auto-initializes, kept for backward compat
export const initializeAnalytics = () => {
  // No-op: analytics are now initialized via cookie consent callbacks
};
